import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, X, Info, AlertCircle, Loader2 } from "lucide-react";
import {
  useCreateBlog,
  type CreateBlogData,
} from "../../../services/blogService";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import ImageUpload from "../../../components/common/ImageUpload";
import RichTextEditor from "../../../components/editor/RichTextEditor";
import { ACCEPTABLE_LANGUAGES } from "../../../constants/languages";

const BlogCreate: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();

  const createBlogMutation = useCreateBlog();

  const [formData, setFormData] = useState<CreateBlogData>({
    status: "draft",
    is_featured: false,
    featured_image: "",
    read_time: undefined,
    published_at: undefined,
    categories: [],
    tags: [],
    translations: ACCEPTABLE_LANGUAGES.map((lang) => ({
      locale: lang,
      title: "",
      excerpt: "",
      body: "",
      slug: "",
      author_title: "",
      author_bio: "",
    })),
  });

  const [currentCategory, setCurrentCategory] = useState("");
  const [currentTag, setCurrentTag] = useState("");
  const [validationError, setValidationError] = useState("");
  const [autoGenerateSlugs, setAutoGenerateSlugs] = useState<{
    [key: string]: boolean;
  }>({});

  // Auto-generate slug from title
  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  // Auto-generate slugs when titles change
  const translationTitles = formData.translations.map((t) => t.title).join(",");
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((translation) => {
        if (autoGenerateSlugs[translation.locale] && translation.title) {
          return {
            ...translation,
            slug: generateSlug(translation.title),
          };
        }
        return translation;
      }),
    }));
  }, [translationTitles, autoGenerateSlugs]);

  // Enable auto-generation when title is first entered
  const handleTitleChange = (locale: string, value: string) => {
    if (!autoGenerateSlugs[locale] && value) {
      setAutoGenerateSlugs((prev) => ({ ...prev, [locale]: true }));
    }
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) =>
        t.locale === locale ? { ...t, title: value } : t
      ),
    }));
  };

  const addCategory = () => {
    if (currentCategory.trim()) {
      setFormData((prev) => ({
        ...prev,
        categories: [...(prev.categories || []), currentCategory.trim()],
      }));
      setCurrentCategory("");
    }
  };

  const removeCategory = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories?.filter((_, i) => i !== index) || [],
    }));
  };

  const addTag = () => {
    if (currentTag.trim()) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), currentTag.trim()],
      }));
      setCurrentTag("");
    }
  };

  const removeTag = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError("");

    // Filter out invalid translations
    const validTranslations = formData.translations.filter(
      (t) =>
        t &&
        t.locale &&
        t.title &&
        t.title.trim() &&
        t.body &&
        t.body.trim() &&
        t.slug &&
        t.slug.trim()
    );

    if (validTranslations.length === 0) {
      setValidationError(
        t(
          "admin.blogs.validation.requiredTranslation",
          "At least one valid translation is required"
        )
      );
      return;
    }

    try {
      await createBlogMutation.mutateAsync({
        ...formData,
        translations: validTranslations,
      });
      navigate(`/${currentLanguage}/admin/blogs`);
    } catch (error) {
      console.error("Failed to create blog:", error);
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { errors?: Record<string, string[]> } };
        };
        if (axiosError.response?.data?.errors) {
          const errors = axiosError.response.data.errors;
          const firstError = Object.values(errors).flat()[0];
          setValidationError(
            firstError ||
            t("admin.blogs.errorCreating", "Failed to create blog post")
          );
        } else {
          setValidationError(
            t("admin.blogs.errorCreating", "Failed to create blog post")
          );
        }
      } else if (error instanceof Error) {
        setValidationError(
          error.message ||
          t("admin.blogs.errorCreating", "Failed to create blog post")
        );
      } else {
        setValidationError(
          t("admin.blogs.errorCreating", "Failed to create blog post")
        );
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4"
        >
          <Button
            variant="ghost"
            onClick={() => navigate(`/${currentLanguage}/admin/blogs`)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("admin.blogs.back", "Back")}
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t("admin.blogs.create", "Create Blog Post")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t("admin.blogs.createSubtitle", "Create a new blog post")}
            </p>
          </div>
        </motion.div>

        {/* Validation Error */}
        {validationError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3"
          >
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-red-600 dark:text-red-400">{validationError}</p>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* General Information */}
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
              <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
                {t("admin.blogs.general", "General Information")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="status">
                    {t("admin.blogs.status", "Status")}
                  </Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        status: e.target.value as
                          | "draft"
                          | "review"
                          | "published"
                          | "archived",
                      }))
                    }
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="draft">
                      {t("admin.blogs.draft", "Draft")}
                    </option>
                    <option value="review">
                      {t("admin.blogs.review", "Review")}
                    </option>
                    <option value="published">
                      {t("admin.blogs.published", "Published")}
                    </option>
                    <option value="archived">
                      {t("admin.blogs.archived", "Archived")}
                    </option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="read_time">
                    {t("admin.blogs.readTime", "Read Time (minutes)")}
                  </Label>
                  <Input
                    id="read_time"
                    type="number"
                    min="1"
                    value={formData.read_time || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        read_time: e.target.value
                          ? parseInt(e.target.value)
                          : undefined,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={formData.is_featured}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_featured: e.target.checked,
                    }))
                  }
                  className="w-4 h-4"
                />
                <Label htmlFor="is_featured">
                  {t("admin.blogs.featured", "Featured Post")}
                </Label>
              </div>
              <div>
                <Label htmlFor="featured_image">
                  {t("admin.blogs.featuredImage", "Featured Image")}
                </Label>
                <ImageUpload
                  value={formData.featured_image || ""}
                  onChange={(url) => {
                    const imageUrl = Array.isArray(url)
                      ? url[0] || ""
                      : url || "";
                    setFormData((prev) => ({
                      ...prev,
                      featured_image: imageUrl,
                    }));
                  }}
                  onUpload={async (files: File[]): Promise<string[]> => {
                    const file = files[0];
                    if (!file) return [];

                    return new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        const result = e.target?.result as string;
                        resolve([result]);
                      };
                      reader.onerror = reject;
                      reader.readAsDataURL(file);
                    });
                  }}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="published_at">
                  {t("admin.blogs.publishedAt", "Published At")}
                </Label>
                <Input
                  id="published_at"
                  type="datetime-local"
                  value={
                    formData.published_at
                      ? new Date(formData.published_at)
                        .toISOString()
                        .slice(0, 16)
                      : ""
                  }
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      published_at: e.target.value
                        ? new Date(e.target.value).toISOString()
                        : undefined,
                    }))
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Categories and Tags */}
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 border-b border-gray-200 dark:border-gray-700">
              <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
                {t("admin.blogs.categoriesTags", "Categories & Tags")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div>
                <Label>{t("admin.blogs.categories", "Categories")}</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={currentCategory}
                    onChange={(e) => setCurrentCategory(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addCategory();
                      }
                    }}
                    placeholder={t(
                      "admin.blogs.addCategory",
                      "Add category..."
                    )}
                  />
                  <Button type="button" onClick={addCategory}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.categories?.map((category, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      {category}
                      <button
                        type="button"
                        onClick={() => removeCategory(index)}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <Label>{t("admin.blogs.tags", "Tags")}</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    placeholder={t("admin.blogs.addTag", "Add tag...")}
                  />
                  <Button type="button" onClick={addTag}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags?.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="flex items-center gap-1"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(index)}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Author Information */}
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-b border-gray-200 dark:border-gray-700">
              <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
                {t("admin.blogs.authorInfo", "Author Information")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="author_name">
                    {t("admin.blogs.customAuthorName", "Custom Author Name")}
                  </Label>
                  <Input
                    id="author_name"
                    value={formData.author_name || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        author_name: e.target.value,
                      }))
                    }
                    placeholder={t(
                      "admin.blogs.authorNamePlaceholder",
                      "Enter contributor name"
                    )}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t(
                      "admin.blogs.authorNameHelp",
                      "Leave empty to use your system name"
                    )}
                  </p>
                </div>
                <div>
                  <Label htmlFor="author_picture">
                    {t("admin.blogs.authorPicture", "Author Picture")}
                  </Label>
                  <ImageUpload
                    value={formData.author_picture || ""}
                    onChange={(url) => {
                      const imageUrl = Array.isArray(url)
                        ? url[0] || ""
                        : url || "";
                      setFormData((prev) => ({
                        ...prev,
                        author_picture: imageUrl,
                      }));
                    }}
                    onUpload={async (files) => {
                      const file = files[0];
                      if (!file) return [];
                      return new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = (e) =>
                          resolve([e.target?.result as string]);
                        reader.readAsDataURL(file);
                      });
                    }}
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Translations */}
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border-b border-gray-200 dark:border-gray-700">
              <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                <Info className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                {t("admin.blogs.translations", "Translations")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {ACCEPTABLE_LANGUAGES.map((lang) => {
                const translation = formData.translations.find(
                  (t) => t.locale === lang
                );
                if (!translation) return null;

                return (
                  <div
                    key={lang}
                    className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-gradient-to-br from-white to-gray-50/50 dark:from-gray-800 dark:to-gray-900/50"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-bold">
                          {lang.toUpperCase()}
                        </span>
                        {lang === "en" ? "English" : "Français"}
                      </h3>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label htmlFor={`title-${lang}`}>
                          {t("admin.blogs.title", "Title")}
                        </Label>
                        <Input
                          id={`title-${lang}`}
                          value={translation.title}
                          onChange={(e) =>
                            handleTitleChange(lang, e.target.value)
                          }
                          placeholder={t(
                            "admin.blogs.titlePlaceholder",
                            "Enter title..."
                          )}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`slug-${lang}`}>
                          {t("admin.blogs.slug", "Slug")}
                        </Label>
                        <div className="relative">
                          <Input
                            id={`slug-${lang}`}
                            value={translation.slug}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                translations: prev.translations.map((t) =>
                                  t.locale === lang
                                    ? { ...t, slug: e.target.value }
                                    : t
                                ),
                              }))
                            }
                            placeholder="blog-post-slug"
                            className="mt-1 font-mono text-sm"
                            onFocus={() =>
                              setAutoGenerateSlugs((prev) => ({
                                ...prev,
                                [lang]: false,
                              }))
                            }
                          />
                          {autoGenerateSlugs[lang] && (
                            <Badge
                              variant="secondary"
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs"
                            >
                              {t("admin.events.autoGenerated", "Auto")}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label htmlFor={`excerpt-${lang}`}>
                          {t("admin.blogs.excerpt", "Excerpt")}
                        </Label>
                        <textarea
                          id={`excerpt-${lang}`}
                          value={translation.excerpt || ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              translations: prev.translations.map((t) =>
                                t.locale === lang
                                  ? { ...t, excerpt: e.target.value }
                                  : t
                              ),
                            }))
                          }
                          placeholder={t(
                            "admin.blogs.excerptPlaceholder",
                            "Enter excerpt..."
                          )}
                          className="w-full min-h-[80px] p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mt-1"
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`body-${lang}`}>
                          {t("admin.blogs.body", "Body Content")}
                        </Label>
                        <RichTextEditor
                          value={translation.body}
                          onChange={(html) =>
                            setFormData((prev) => ({
                              ...prev,
                              translations: prev.translations.map((t) =>
                                t.locale === lang ? { ...t, body: html } : t
                              ),
                            }))
                          }
                          placeholder={t(
                            "admin.blogs.bodyPlaceholder",
                            "Start writing..."
                          )}
                          className="mt-2"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                        <div>
                          <Label htmlFor={`author_title-${lang}`}>
                            {t("admin.blogs.authorTitle", "Author Title/Role")}
                          </Label>
                          <Input
                            id={`author_title-${lang}`}
                            value={translation.author_title || ""}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                translations: prev.translations.map((t) =>
                                  t.locale === lang
                                    ? { ...t, author_title: e.target.value }
                                    : t
                                ),
                              }))
                            }
                            placeholder={t(
                              "admin.blogs.authorTitlePlaceholder",
                              "e.g., Community Contributor"
                            )}
                          />
                        </div>
                        <div>
                          <Label htmlFor={`author_bio-${lang}`}>
                            {t("admin.blogs.authorBio", "Author Bio")}
                          </Label>
                          <textarea
                            id={`author_bio-${lang}`}
                            value={translation.author_bio || ""}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                translations: prev.translations.map((t) =>
                                  t.locale === lang
                                    ? { ...t, author_bio: e.target.value }
                                    : t
                                ),
                              }))
                            }
                            placeholder={t(
                              "admin.blogs.authorBioPlaceholder",
                              "Brief bio of the contributor..."
                            )}
                            className="w-full min-h-[80px] p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mt-1"
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/${currentLanguage}/admin/blogs`)}
            >
              {t("admin.blogs.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              disabled={createBlogMutation.isPending}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {createBlogMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("admin.blogs.creating", "Creating...")}
                </>
              ) : (
                t("admin.blogs.create", "Create Post")
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogCreate;
